import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface JobUpdate {
  id: string;
  type: 'application' | 'status_change' | 'assignment' | 'completion';
  title: string;
  message: string;
  timestamp: string;
}

interface UseRealtimeJobUpdatesProps {
  userId: string;
  onUpdate?: (update: JobUpdate) => void;
}

export const useRealtimeJobUpdates = ({ userId, onUpdate }: UseRealtimeJobUpdatesProps) => {
  const handleJobUpdate = useCallback((payload: any) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;
    
    let update: JobUpdate | null = null;
    
    switch (eventType) {
      case 'INSERT':
        if (newRecord.table === 'job_applications' && newRecord.employer_id === userId) {
          update = {
            id: newRecord.id,
            type: 'application',
            title: 'New Job Application',
            message: `Someone applied to your job`,
            timestamp: new Date().toISOString()
          };
        }
        break;
        
      case 'UPDATE':
        if (newRecord.table === 'jobs' && (newRecord.employer_id === userId || newRecord.assigned_to === userId)) {
          if (oldRecord.status !== newRecord.status) {
            update = {
              id: newRecord.id,
              type: 'status_change',
              title: 'Job Status Updated',
              message: `Job status changed to ${newRecord.status}`,
              timestamp: new Date().toISOString()
            };
          }
        }
        break;
    }
    
    if (update) {
      onUpdate?.(update);
      
      // Show toast notification
      toast.success(update.title, {
        description: update.message,
        duration: 5000,
      });
    }
  }, [userId, onUpdate]);

  useEffect(() => {
    // Subscribe to job applications
    const applicationsChannel = supabase
      .channel('job-applications-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'job_applications',
          filter: `employer_id=eq.${userId}`
        },
        handleJobUpdate
      )
      .subscribe();

    // Subscribe to job status changes
    const jobsChannel = supabase
      .channel('jobs-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'jobs',
          filter: `employer_id=eq.${userId}`
        },
        handleJobUpdate
      )
      .subscribe();

    // Subscribe to escrow updates
    const escrowChannel = supabase
      .channel('escrow-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'escrow_transactions',
          filter: `employer_id=eq.${userId}`
        },
        handleJobUpdate
      )
      .subscribe();

    return () => {
      supabase.removeChannel(applicationsChannel);
      supabase.removeChannel(jobsChannel);
      supabase.removeChannel(escrowChannel);
    };
  }, [userId, handleJobUpdate]);

  return {
    // Additional methods can be added here for manual updates
  };
};